import React from "react";
import { Funnel } from "lucide-react";
import GenericButton from "@ui-components/Button/GenericButton";
import { LogItem } from "./LogsData";
import { parseLogAttributes } from "./logAttributes";
import { useCopyFeedback } from "./useCopyFeedback";

/** Adds a chip for `label` and `value`; each page decides what chip that is. */
export type FilterByAttribute = (label: string, value: string) => void;

/** What the page knows about a line besides its message: a label and its value. */
export interface ContextAttribute {
  label: string;
  value: string;
}

interface LogRowDetailsProps {
  /** The original message; the attributes parsed out of it come first. */
  message: string;
  /** Shown after those, in this order: V1 the CTX-R, V2 the Reflector; both the timestamp. */
  contextAttributes: ContextAttribute[];
  /** What Copy JSON puts on the clipboard. */
  json: string;
  /** Called with the label and value of the attribute whose filter button was clicked. */
  onFilter: FilterByAttribute;
}

/**
 * V1's Copy JSON: the row as the handler sent it, so the timestamp is the raw
 * one, not the local time the table shows.
 */
export function logRowJson(row: LogItem, ctxr: string): string {
  return JSON.stringify(
    { timestamp: row.rawTimestamp, level: row.level, message: row.message, ctxr },
    null,
    2
  );
}

const SECTION_LABEL_CLASS = "text-xs uppercase tracking-wide text-semantic-text-secondary";

function CopyTextButton({ label, text }: { label: string; text: string }) {
  const { copied, copy } = useCopyFeedback();
  return (
    <GenericButton
      label={copied ? "Copied" : label}
      onClick={() => copy(text)}
      variant="secondary"
      size="sm"
    />
  );
}

interface AttributeProps {
  label: string;
  value: string;
  onFilter: FilterByAttribute;
}

// An empty value gets no button: an empty chip filters nothing.
function Attribute({ label, value, onFilter }: AttributeProps) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className={SECTION_LABEL_CLASS}>{label}</dt>
      <dd className="flex items-center gap-1 text-sm text-semantic-text-primary">
        <span className="min-w-0 break-all">{value || "—"}</span>
        {value && (
          <GenericButton
            onClick={() => onFilter(label, value)}
            icon={<Funnel className="w-3.5 h-3.5 text-semantic-text-secondary" aria-hidden />}
            isIconOnly
            variant="secondary"
            shape="ghost"
            size="xsm"
            className="flex-shrink-0"
            title={`Filter by ${label}`}
            aria-label={`Filter by ${label}`}
          />
        )}
      </dd>
    </div>
  );
}

/**
 * The expanded log row, stacked to keep it short: the attributes in a wrapping
 * grid on top, the original message full width below. The attributes are the ones parsed out of the message (CTX-1356),
 * then the page's own (CTX-1484: the CTX-R in V1, the Reflector in V2, and the
 * timestamp); each one's filter button hands it to `onFilter`. The message is
 * rendered as text, never as HTML.
 */
export function LogRowDetails({ message, contextAttributes, json, onFilter }: LogRowDetailsProps) {
  return (
    <div className="my-2 flex flex-col gap-3 rounded-md border border-semantic-border-secondary bg-semantic-bg-primary-alt p-4">
      <section aria-label="Attributes">
        <h4 className={`mb-1 ${SECTION_LABEL_CLASS}`}>Attributes</h4>
        <dl className="grid gap-x-6 gap-y-2 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
          {parseLogAttributes(message).map(({ key, label, value }) => (
            <Attribute key={key} label={label} value={value} onFilter={onFilter} />
          ))}
          {contextAttributes.map(({ label, value }) => (
            <Attribute key={label} label={label} value={value} onFilter={onFilter} />
          ))}
        </dl>
      </section>

      <section aria-label="Original message" className="min-w-0">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <h4 className={SECTION_LABEL_CLASS}>Original message</h4>
          <div className="flex gap-2">
            <CopyTextButton label="Copy" text={message} />
            <CopyTextButton label="Copy JSON" text={json} />
          </div>
        </div>
        <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap break-words rounded-md border border-semantic-border-primary bg-semantic-bg-primary p-3 font-mono text-xs text-semantic-text-primary">
          {message}
        </pre>
      </section>
    </div>
  );
}
